import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import { api, formatError } from "../../../lib/api";
import { useSettings } from "../../../lib/providers";

export const Group = ({ title, desc, children }) => (
  <div className="lux-card space-y-4 p-6">
    <div><h3 className="font-serif text-xl font-semibold">{title}</h3>{desc && <p className="mt-1 text-sm text-muted-foreground">{desc}</p>}</div>
    {children}
  </div>
);

export const Field = ({ label, value, onChange, testId, type = "text", ...rest }) => (
  <div className="space-y-1.5"><Label>{label}</Label><Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} {...rest} /></div>
);

export const Toggle = ({ label, desc, checked, onChange, testId }) => (
  <label className="flex items-center justify-between gap-4 rounded-xl border p-4">
    <span><span className="block text-sm font-medium">{label}</span>{desc && <span className="block text-xs text-muted-foreground">{desc}</span>}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
  </label>
);

export const useSectionSave = () => {
  const { settings, setSettings } = useSettings();
  const [busy, setBusy] = useState(false);
  const save = async (partial) => {
    setBusy(true);
    try { const { data } = await api.put("/admin/settings", partial); setSettings(data); toast.success("Saved — changes are live on the website."); }
    catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  return { settings, save, busy };
};

export const useDraft = (value) => {
  const [d, setD] = useState(() => structuredClone(value));
  useEffect(() => { setD(structuredClone(value)); }, [value]);
  return [d, setD];
};

export const SaveBar = ({ onClick, busy, testId }) => (
  <button type="button" onClick={onClick} disabled={busy} className="btn-gold" data-testid={testId}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save changes</button>
);

export const UploadField = ({ label, kind, accept, path, onChange, testId, preview, hint }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.path);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5"><Label>{label}</Label>
      <div className="flex flex-wrap items-center gap-3">
        {path && preview}
        <label className="btn-ghost-gold cursor-pointer !py-2" data-testid={`${testId}-btn`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Upload
          <input type="file" accept={accept} className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid={`${testId}-input`} />
        </label>
        {path && <button type="button" onClick={() => onChange(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-destructive" data-testid={`${testId}-remove`}><X className="h-4 w-4" />Remove</button>}
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
};

export const move = (list, i, d) => {
  const next = [...list];
  const j = i + d;
  if (j < 0 || j >= next.length) return list;
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

export const RowTools = ({ onUp, onDown, onDelete, testId }) => (
  <div className="flex gap-1.5">
    <button type="button" onClick={onUp} className="flex h-8 w-8 items-center justify-center rounded-full border hover:bg-accent" title="Move up" data-testid={`${testId}-up`}><ArrowUp className="h-4 w-4" /></button>
    <button type="button" onClick={onDown} className="flex h-8 w-8 items-center justify-center rounded-full border hover:bg-accent" title="Move down" data-testid={`${testId}-down`}><ArrowDown className="h-4 w-4" /></button>
    <button type="button" onClick={onDelete} className="flex h-8 w-8 items-center justify-center rounded-full border text-red-500 hover:bg-accent" title="Delete" data-testid={`${testId}-delete`}><Trash2 className="h-4 w-4" /></button>
  </div>
);
